import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Star, Trash2, History, RefreshCcw, Dumbbell, BarChart3, ClipboardCheck, MessageSquare, Eye, AlertCircle, Loader2, Search,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const KIND_META = {
  training: { label: "Allenamento", Icon: Dumbbell, accent: "text-sky-600 bg-sky-50" },
  "match-analysis": { label: "Analisi partita", Icon: BarChart3, accent: "text-violet-600 bg-violet-50" },
  "callup-suggestion": { label: "Convocazione", Icon: ClipboardCheck, accent: "text-emerald-600 bg-emerald-50" },
  "parents-message": { label: "Messaggio", Icon: MessageSquare, accent: "text-amber-600 bg-amber-50" },
};

const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

function jobTitle(job) {
  if (job.title) return job.title;
  const p = job.payload || {};
  if (job.kind === "training") return p.focus || "Allenamento";
  if (job.kind === "callup-suggestion") return p.opponent ? `vs ${p.opponent}` : "Convocazione";
  if (job.kind === "match-analysis") return "Analisi partita";
  if (job.kind === "parents-message") return `Messaggio · ${p.tone || "cordiale"}`;
  return job.kind;
}

/**
 * AI generation history tab. Displays past jobs with filters, favorites,
 * delete, and a "re-run" callback that lets the parent tab pre-fill its form.
 */
export default function AIHistoryTab({ team, onRerun }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ kind: "", favorites: false, search: "" });
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter.kind) params.kind = filter.kind;
      if (filter.favorites) params.favorites_only = true;
      const { data } = await api.get(`/teams/${team.id}/ai-jobs`, { params });
      setItems(data);
    } catch (e) {
      toast.error("Errore caricamento cronologia");
    }
    setLoading(false);
  }, [team.id, filter.kind, filter.favorites]);

  useEffect(() => { load(); }, [load]);

  const toggleFav = async (job) => {
    try {
      const { data } = await api.patch(`/ai/jobs/${job.id}`, { favorite: !job.favorite });
      setItems((arr) => arr.map((i) => (i.id === job.id ? { ...i, favorite: data.favorite } : i)));
      toast.success(data.favorite ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm("Eliminare questa generazione dalla cronologia?")) return;
    try {
      await api.delete(`/ai/jobs/${job.id}`);
      setItems((arr) => arr.filter((i) => i.id !== job.id));
      toast.success("Generazione eliminata");
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const setTitle = async (job) => {
    const t = window.prompt("Titolo personalizzato:", jobTitle(job));
    if (t == null) return;
    try {
      const { data } = await api.patch(`/ai/jobs/${job.id}`, { favorite: !!job.favorite, title: t });
      setItems((arr) => arr.map((i) => (i.id === job.id ? { ...i, title: data.title } : i)));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const search = filter.search.trim().toLowerCase();
  const filtered = items.filter((j) => {
    if (!search) return true;
    return (
      jobTitle(j).toLowerCase().includes(search)
      || (j.kind || "").includes(search)
      || JSON.stringify(j.payload || {}).toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-4" data-testid="ai-history-tab">
      <Card className="p-4 border-slate-200">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 mr-1">
            <History size={16} className="text-slate-500" />
            <span className="font-heading font-semibold text-sm">Cronologia AI</span>
            <span className="text-xs text-slate-500">· {items.length} generazioni</span>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="inline-flex p-0.5 bg-slate-100 rounded-lg text-xs">
              <button
                onClick={() => setFilter((f) => ({ ...f, kind: "" }))}
                className={`px-3 py-1 rounded-md ${filter.kind === "" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
              >Tutto</button>
              {Object.entries(KIND_META).map(([k, meta]) => (
                <button
                  key={k}
                  onClick={() => setFilter((f) => ({ ...f, kind: k }))}
                  className={`px-3 py-1 rounded-md inline-flex items-center gap-1 ${filter.kind === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  data-testid={`history-filter-${k}`}
                >
                  <meta.Icon size={11} />{meta.label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant={filter.favorites ? "default" : "outline"}
              onClick={() => setFilter((f) => ({ ...f, favorites: !f.favorites }))}
              className={filter.favorites ? "bg-amber-500 hover:bg-amber-600" : ""}
              data-testid="history-favorites-toggle"
            >
              <Star size={13} className={`mr-1 ${filter.favorites ? "fill-current" : ""}`} /> Preferiti
            </Button>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="h-8">
              <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
        <div className="mt-3 relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={filter.search}
            onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            placeholder="Cerca per focus, avversario, tono..."
            className="pl-8"
            data-testid="history-search"
          />
        </div>
      </Card>

      {loading && (
        <Card className="p-8 border-slate-200 text-center">
          <Loader2 size={24} className="animate-spin mx-auto text-slate-400" />
        </Card>
      )}

      {!loading && filtered.length === 0 && (
        <Card className="p-10 border-slate-200 text-center">
          <History size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-700">Nessuna generazione</p>
          <p className="text-xs text-slate-500 mt-1">
            {filter.search || filter.kind || filter.favorites
              ? "Prova a modificare i filtri."
              : "Le generazioni di Mister AI compariranno qui."}
          </p>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid gap-2">
          {filtered.map((job) => {
            const meta = KIND_META[job.kind] || { label: job.kind, Icon: AlertCircle, accent: "text-slate-500 bg-slate-50" };
            const Icon = meta.Icon;
            const errored = job.status === "error";
            return (
              <Card key={job.id} className={`p-3 border-slate-200 ${errored ? "opacity-70" : ""}`} data-testid={`history-row-${job.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 w-9 h-9 rounded-xl ${meta.accent} grid place-items-center`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <button onClick={() => setTitle(job)} className="text-sm font-semibold text-slate-800 truncate hover:underline text-left">
                        {jobTitle(job)}
                      </button>
                      {errored && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">errore</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {meta.label} · {fmtDateTime(job.created_at)}
                      {job.payload?.duration_min ? ` · ${job.payload.duration_min} min` : ""}
                      {job.payload?.n_drills ? ` · ${job.payload.n_drills} esercizi` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => toggleFav(job)} title={job.favorite ? "Rimuovi preferito" : "Aggiungi ai preferiti"} className="h-8 w-8" data-testid={`history-fav-${job.id}`}>
                      <Star size={14} className={job.favorite ? "fill-amber-400 text-amber-500" : "text-slate-400"} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setViewing(job)} title="Visualizza risultato" disabled={errored} className="h-8 w-8" data-testid={`history-view-${job.id}`}>
                      <Eye size={14} className="text-slate-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2"
                      onClick={() => onRerun?.(job)}
                      title="Rigenera con questi parametri"
                      disabled={!onRerun || errored}
                      data-testid={`history-rerun-${job.id}`}
                    >
                      <RefreshCcw size={12} className="mr-1" /> Rigenera
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeJob(job)} className="h-8 w-8" data-testid={`history-delete-${job.id}`}>
                      <Trash2 size={13} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="history-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{viewing && jobTitle(viewing)}</DialogTitle>
            <DialogDescription>
              {viewing && (KIND_META[viewing.kind]?.label || viewing.kind)} · {viewing && fmtDateTime(viewing.created_at)}
            </DialogDescription>
          </DialogHeader>
          {viewing && <ResultPreview job={viewing} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Chiudi</Button>
            {viewing && onRerun && (
              <Button onClick={() => { onRerun(viewing); setViewing(null); }} className="bg-sky-500 hover:bg-sky-600">
                <RefreshCcw size={14} className="mr-1" /> Rigenera
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultPreview({ job }) {
  const r = job.result || {};
  if (job.kind === "training") {
    return (
      <div className="space-y-3 text-sm">
        {r.title && <h3 className="font-heading font-bold text-lg">{r.title}</h3>}
        {r.objectives && <div className="p-3 bg-sky-50 rounded-lg"><strong>Obiettivi: </strong>{r.objectives}</div>}
        {r.warm_up && <div><strong>Riscaldamento:</strong> <p className="text-slate-600 whitespace-pre-line">{r.warm_up}</p></div>}
        {(r.drills || []).map((d, i) => (
          <div key={i} className="p-3 border rounded-lg">
            <strong>{d.name}</strong> <span className="text-xs text-slate-500 ml-1">{d.duration_min} min</span>
            <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{d.description}</p>
          </div>
        ))}
        {r.cool_down && <div><strong>Defaticamento:</strong> <p className="text-slate-600 whitespace-pre-line">{r.cool_down}</p></div>}
        {r.coach_notes && <div className="p-3 bg-amber-50 rounded-lg text-xs"><strong>Note: </strong>{r.coach_notes}</div>}
      </div>
    );
  }
  if (job.kind === "parents-message") {
    return <pre className="text-sm whitespace-pre-wrap bg-slate-50 p-4 rounded-lg font-mono">{r.text || "(vuoto)"}</pre>;
  }
  if (job.kind === "callup-suggestion") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        {[["called_up", "Convocati", "emerald"], ["borderline", "Borderline", "amber"], ["not_called", "Esclusi", "slate"]].map(([k, label, color]) => (
          <div key={k} className={`p-3 rounded-lg border border-${color}-200 bg-${color}-50/30`}>
            <strong className={`text-${color}-700`}>{label} ({(r[k] || []).length})</strong>
            <ul className="mt-1 text-xs space-y-0.5">
              {(r[k] || []).map((p, i) => <li key={i}><span className="font-medium">{p.name || p.player_id}</span> — <span className="text-slate-500">{p.reason}</span></li>)}
            </ul>
          </div>
        ))}
        {r.rationale && <div className="md:col-span-3 p-3 bg-sky-50 rounded-lg text-sm"><strong>Logica: </strong>{r.rationale}</div>}
      </div>
    );
  }
  if (job.kind === "match-analysis") {
    return (
      <div className="space-y-3 text-sm">
        {r.summary && <p className="p-3 bg-sky-50 rounded-lg"><strong>Sintesi: </strong>{r.summary}</p>}
        {r.what_worked && <div><strong className="text-emerald-700">Funzionato:</strong> <ul className="list-disc list-inside text-slate-600">{(Array.isArray(r.what_worked) ? r.what_worked : [r.what_worked]).map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        {r.what_to_improve && <div><strong className="text-amber-700">Da migliorare:</strong> <ul className="list-disc list-inside text-slate-600">{(Array.isArray(r.what_to_improve) ? r.what_to_improve : [r.what_to_improve]).map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        {r.next_training_focus && <div className="p-3 bg-emerald-50 rounded-lg"><strong>Focus prossimo allenamento: </strong>{r.next_training_focus}</div>}
      </div>
    );
  }
  return <pre className="text-xs whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">{JSON.stringify(r, null, 2)}</pre>;
}
